import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
} from 'react'

/* eslint-disable react-refresh/only-export-components */

export type TranslationMessages = Record<string, string>

declare global {
  interface Window {
    I18N_MESSAGES?: Record<string, TranslationMessages>
  }
}

const fallbackLanguageCode = 'en'

function normalizeLanguageCode(languageCode: string): string {
  const normalized = languageCode.replace('-', '_')
  return normalized.toLowerCase() === 'zh_tw' ? 'zh_TW' : normalized.toLowerCase()
}

export function getLoadedMessages(languageCode: string): TranslationMessages {
  const normalized = normalizeLanguageCode(languageCode)
  return window.I18N_MESSAGES?.[normalized]
    ?? window.I18N_MESSAGES?.[fallbackLanguageCode]
    ?? {}
}

export async function loadLanguageMessages(
  languageCode: string,
): Promise<TranslationMessages> {
  const normalized = normalizeLanguageCode(languageCode)
  const loaded = window.I18N_MESSAGES?.[normalized]

  if (loaded)
    return loaded

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-signup-language="${normalized}"]`,
    )

    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(), { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = `/modern-app/i18n/messages_${encodeURIComponent(normalized)}.js`
    script.dataset.signupLanguage = normalized
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Unable to load ${normalized} translations.`))
    document.head.appendChild(script)
  })

  return getLoadedMessages(normalized)
}

export function translate(
  messages: TranslationMessages,
  key: string,
  ...values: Array<string | number>
): string {
  const fallbackMessages = window.I18N_MESSAGES?.[fallbackLanguageCode] ?? {}
  const template = messages[key] ?? fallbackMessages[key] ?? key

  let result = template
  values.forEach((value, index) => {
    result = result.replaceAll(`{${index}}`, String(value))
  })
  return result
}

interface I18nContextValue {
  languageCode: string
  messages: TranslationMessages
  t: (key: string, ...values: Array<string | number>) => string
}

const I18nContext = createContext<I18nContextValue>({
  languageCode: fallbackLanguageCode,
  messages: {},
  t: (key) => key,
})

export function I18nProvider({
  children,
  languageCode,
  messages,
}: {
  children: ReactNode
  languageCode: string
  messages: TranslationMessages
}) {
  const value = useMemo<I18nContextValue>(() => ({
    languageCode,
    messages,
    t: (key, ...values) => translate(messages, key, ...values),
  }), [languageCode, messages])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext)
}

function escapeHtml(value: string | number): string {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}

export function TranslatedHtml({
  as: Element = 'span',
  className,
  messageKey,
  values = [],
}: {
  as?: 'div' | 'p' | 'span'
  className?: string
  messageKey: string
  values?: Array<string | number>
}) {
  const { messages } = useI18n()
  const html = translate(messages, messageKey, ...values.map(escapeHtml))

  return (
    <Element
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
